import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IoLogoWhatsapp } from "react-icons/io";
import { useAuth } from "../../Hooks/AuthContext";
import CountryPicker from "../CountryPicker/CountryPicker";
import {
  DEFAULT_CODE,
  findCountry,
  maskMobile,
  maxLen,
  isCompleteNumber,
  toNationalNumber,
} from "../../Utils/countries";
import {
  AUTH_CODES,
  MAX_PASSWORD,
  authErrorCode,
  authErrorMessage,
  isPasswordValid,
  isPlausibleMobile,
} from "../../Utils/passwordAuth";

/**
 * Phone + password login, shared by the checkout LoginSheet, the nav-drawer login and the
 * /auth/login page so the three can't drift apart.
 *
 * Steps: "login" → (no password yet, or Forgot password) → "code" (WhatsApp code + new password).
 * In "account" mode there is also "signup" (phone + password, no code — the backend only allows
 * it for a number that has never been used).
 *
 * mode="checkout": no sign-up. An unknown number gets "Continue as guest" instead, because a
 *   shopper at checkout never needs an account to buy.
 * mode="account": menu / login page; an unknown number is offered "Create account".
 *
 * onSuccess(result) runs after AuthContext has set `user`. result = { isReturning, name, raw }.
 */

const RESEND_SECONDS = 30;
const EMPTY_CODE = ["", "", "", ""];

const SIZES = {
  normal: {
    field: "rounded-md px-3 py-3 text-[14px]",
    label: "text-[10.5px] mb-1.5",
    button: "rounded-md py-3 text-[14px]",
    small: "text-[11.5px]",
    title: "text-[17px]",
  },
  compact: {
    field: "rounded-[10px] px-[11px] py-[10px] text-[13px]",
    label: "text-[10px] mb-1",
    button: "rounded-[10px] py-[11px] text-[13px]",
    small: "text-[10.5px]",
    title: "text-[13px]",
  },
};

const PasswordLoginForm = ({
  mode = "account",
  compact = false,
  onSuccess,
  onGuest,
  autoFocus = false,
  focusTick = 0,
}) => {
  const { t } = useTranslation();
  const { loginWithPassword, signupWithPassword, sendPasswordCode, setPassword } =
    useAuth();
  const geoCode = useSelector((state) => state.location?.countryCode);
  const sz = compact ? SIZES.compact : SIZES.normal;

  const [step, setStep] = useState("login");
  const [purpose, setPurpose] = useState("reset"); // "set" | "reset" (code step copy)
  const [country, setCountry] = useState(() =>
    findCountry(geoCode || DEFAULT_CODE),
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mobile, setMobile] = useState("");
  const [password, setPasswordValue] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState(EMPTY_CODE); // one digit per box
  const codeValue = code.join("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [guestNotice, setGuestNotice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const phoneRef = useRef(null);
  const boxRefs = useRef([]);

  useEffect(() => {
    if (autoFocus) phoneRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (focusTick > 0 && step !== "code") phoneRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTick]);

  useEffect(() => {
    if (step !== "code") return undefined;
    const id = setTimeout(() => boxRefs.current[0]?.focus(), 60);
    return () => clearTimeout(id);
  }, [step]);

  // 4-box code entry — same interaction as the old WhatsApp-OTP screens: one digit per box,
  // auto-advance, Backspace steps back, pasting a code fills every box.
  const setDigit = (i, val) => {
    const clean = val.replace(/\D/g, "");
    if (!clean && val) return;
    const next = [...code];
    next[i] = clean.slice(-1);
    setCode(next);
    setError("");
    if (clean && i < 3) boxRefs.current[i + 1]?.focus();
  };

  const onCodeKeyDown = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) boxRefs.current[i - 1]?.focus();
    if (e.key === "Enter") submitCode();
  };

  const onCodePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!pasted) return;
    e.preventDefault();
    const next = [...EMPTY_CODE];
    pasted.split("").forEach((d, idx) => (next[idx] = d));
    setCode(next);
    setError("");
    boxRefs.current[Math.min(pasted.length, 3)]?.focus();
  };

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const numberComplete = isCompleteNumber(country, mobile);
  const numberOk = numberComplete && isPlausibleMobile(country.dial, mobile);
  // Right length but can't be a real number (e.g. +91 starting with 0) → say so under the field.
  const numberInvalid = numberComplete && !numberOk;

  const clearMessages = () => {
    setError("");
    setGuestNotice(false);
  };

  const done = (result) => {
    setBusy(false);
    onSuccess?.(result);
  };

  const sendCode = (nextPurpose) => {
    setPurpose(nextPurpose);
    setBusy(true);
    clearMessages();
    return Promise.resolve(sendPasswordCode(mobile, country.dial))
      .then(() => {
        setStep("code");
        setCode(EMPTY_CODE);
        setCooldown(RESEND_SECONDS);
      })
      .catch((err) => handleError(err, t("passwordLogin.sendFailed")))
      .finally(() => setBusy(false));
  };

  // One place that turns a backend refusal into the next screen.
  const handleError = (err, fallback) => {
    setBusy(false);
    switch (authErrorCode(err)) {
      case AUTH_CODES.NO_ACCOUNT:
        if (mode === "checkout") {
          setGuestNotice(true);
        } else {
          setStep("signup");
          setError(t("passwordLogin.noAccountCreate"));
        }
        return;
      case AUTH_CODES.PASSWORD_NOT_SET:
        // Ordered before (guest / old OTP login) but never set a password: verify the number
        // once on WhatsApp. Keep what they typed as the new password so it's one less field.
        setNewPassword(isPasswordValid(password) ? password : "");
        sendCode("set");
        return;
      case AUTH_CODES.ACCOUNT_EXISTS:
        setStep("login");
        setError(authErrorMessage(err) || t("passwordLogin.genericError"));
        return;
      case AUTH_CODES.WRONG_CODE:
        setCode(EMPTY_CODE);
        setError(authErrorMessage(err) || t("passwordLogin.genericError"));
        return;
      default:
        setError(authErrorMessage(err) || fallback || t("passwordLogin.genericError"));
    }
  };

  const submitLogin = () => {
    if (!numberOk || busy) return;
    if (!password) {
      setError(t("passwordLogin.enterPassword"));
      return;
    }
    setBusy(true);
    clearMessages();
    Promise.resolve(loginWithPassword(mobile, password, country.dial))
      .then(done)
      .catch((err) => handleError(err));
  };

  const submitSignup = () => {
    if (!numberOk || busy) return;
    if (!isPasswordValid(password)) {
      setError(t("passwordLogin.passwordTooShort"));
      return;
    }
    setBusy(true);
    clearMessages();
    Promise.resolve(signupWithPassword(mobile, password, country.dial))
      .then(done)
      .catch((err) => handleError(err));
  };

  const submitCode = () => {
    if (busy || codeValue.length !== 4) return;
    if (!isPasswordValid(newPassword)) {
      setError(t("passwordLogin.passwordTooShort"));
      return;
    }
    setBusy(true);
    clearMessages();
    Promise.resolve(setPassword(mobile, codeValue, newPassword, country.dial))
      .then(done)
      .catch((err) => handleError(err));
  };

  const forgot = () => {
    if (busy) return;
    if (!numberOk) {
      setError(t("passwordLogin.enterNumberFirst"));
      phoneRef.current?.focus();
      return;
    }
    setNewPassword("");
    sendCode("reset");
  };

  const resend = () => {
    if (cooldown > 0 || busy) return;
    setError("");
    setCooldown(RESEND_SECONDS);
    Promise.resolve(sendPasswordCode(mobile, country.dial)).catch((err) =>
      setError(authErrorMessage(err) || t("passwordLogin.sendFailed")),
    );
  };

  const goTo = (next) => {
    setStep(next);
    clearMessages();
  };

  const Label = ({ children }) => (
    <label
      className={`block font-[500] tracking-wide uppercase text-ink-400 ${sz.label}`}
    >
      {children}
    </label>
  );

  const PasswordInput = ({ value, onChange, onEnter, testId, autoComplete, placeholder }) => (
    <div
      className={`flex items-center gap-2 border-[1.5px] border-wc-border focus-within:border-s-primary bg-white ${sz.field}`}
    >
      <input
        data-testid={testId}
        type={showPw ? "text" : "password"}
        autoComplete={autoComplete}
        placeholder={placeholder}
        maxLength={MAX_PASSWORD}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onEnter()}
        className="flex-1 min-w-0 font-[400] text-ink-900 outline-none bg-transparent placeholder:text-ink-400"
      />
      <button
        type="button"
        onClick={() => setShowPw((s) => !s)}
        className={`text-s-primary font-[500] ${sz.small}`}
      >
        {showPw ? t("passwordLogin.hide") : t("passwordLogin.show")}
      </button>
    </div>
  );

  const PrimaryBtn = ({ onClick, disabled, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-2 w-full mt-3 bg-s-primary text-white font-[500] disabled:opacity-50 ${sz.button}`}
    >
      {busy && (
        <span className="inline-block w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
      )}
      {children}
    </button>
  );

  const Message = () =>
    error ? (
      <p className={`text-coral-ink font-[500] mt-2 ${sz.small}`} role="alert">
        {error}
      </p>
    ) : null;

  const phoneField = (
    <div
      className={`flex items-center gap-2.5 border-[1.5px] border-wc-border focus-within:border-s-primary bg-white ${sz.field}`}
    >
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className="flex items-center gap-1 font-[500] text-ink-900 pr-2.5 border-r border-wc-border"
      >
        +{country.dial}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className="w-[10px] h-[10px] text-ink-400"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <input
        ref={phoneRef}
        data-testid="pw-mobile"
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        placeholder={country.sample || t("passwordLogin.mobileNumber")}
        value={mobile}
        onChange={(e) => {
          setMobile(toNationalNumber(e.target.value, country));
          clearMessages();
        }}
        className="flex-1 min-w-0 font-[400] text-ink-900 tracking-wide outline-none bg-transparent placeholder:text-ink-400"
      />
    </div>
  );
  const phoneBlock = (
    <>
      {phoneField}
      {numberInvalid && (
        <p className={`text-coral-ink font-[500] mt-1.5 ${sz.small}`} role="alert">
          {t("passwordLogin.invalidMobile")}
        </p>
      )}
    </>
  );

  return (
    <>
      {step === "login" && (
        <div>
          <Label>{t("passwordLogin.mobileNumber")}</Label>
          {phoneBlock}
          <div className="mt-3">
            <Label>{t("passwordLogin.password")}</Label>
            {PasswordInput({
              value: password,
              onChange: (v) => {
                setPasswordValue(v);
                setError("");
              },
              onEnter: submitLogin,
              testId: "pw-password",
              autoComplete: "current-password",
              placeholder: t("passwordLogin.passwordPlaceholder"),
            })}
          </div>
          <div className="flex justify-end mt-1.5">
            <button
              type="button"
              onClick={forgot}
              className={`text-s-primary font-[500] ${sz.small}`}
            >
              {t("passwordLogin.forgot")}
            </button>
          </div>

          <Message />

          {guestNotice ? (
            <div className="mt-3 rounded-md bg-s-tint px-3 py-2.5">
              <p className={`text-ink-900 font-[500] ${sz.small}`}>
                {t("passwordLogin.noAccountGuest")}
              </p>
              <p className={`text-ink-500 mt-0.5 leading-[1.5] ${sz.small}`}>
                {t("passwordLogin.guestSub")}
              </p>
              <PrimaryBtn onClick={() => onGuest?.()}>
                {t("passwordLogin.continueGuest")}
              </PrimaryBtn>
            </div>
          ) : (
            <PrimaryBtn onClick={submitLogin} disabled={!numberOk || busy}>
              {busy ? t("passwordLogin.loggingIn") : t("passwordLogin.logIn")}
            </PrimaryBtn>
          )}

          {mode === "account" && (
            <p className={`text-center text-ink-500 mt-3 ${sz.small}`}>
              {t("passwordLogin.newHere")}{" "}
              <button
                type="button"
                onClick={() => goTo("signup")}
                className="text-s-primary font-[500]"
              >
                {t("passwordLogin.createAccount")}
              </button>
            </p>
          )}
        </div>
      )}

      {step === "signup" && (
        <div>
          <div className={`font-[500] text-ink-900 mb-2 ${sz.title}`}>
            {t("passwordLogin.signupTitle")}
          </div>
          <Label>{t("passwordLogin.mobileNumber")}</Label>
          {phoneBlock}
          <div className="mt-3">
            <Label>{t("passwordLogin.choosePassword")}</Label>
            {PasswordInput({
              value: password,
              onChange: (v) => {
                setPasswordValue(v);
                setError("");
              },
              onEnter: submitSignup,
              testId: "pw-password",
              autoComplete: "new-password",
              placeholder: t("passwordLogin.newPasswordPlaceholder"),
            })}
            <p className={`text-ink-400 mt-1 ${sz.small}`}>
              {t("passwordLogin.passwordHint")}
            </p>
          </div>
          <Message />
          <PrimaryBtn onClick={submitSignup} disabled={!numberOk || busy}>
            {busy ? t("passwordLogin.creating") : t("passwordLogin.createAccount")}
          </PrimaryBtn>
          <p className={`text-center text-ink-500 mt-3 ${sz.small}`}>
            {t("passwordLogin.haveAccount")}{" "}
            <button
              type="button"
              onClick={() => goTo("login")}
              className="text-s-primary font-[500]"
            >
              {t("passwordLogin.logIn")}
            </button>
          </p>
        </div>
      )}

      {step === "code" && (
        <div>
          <div className={`font-[500] text-ink-900 ${sz.title}`}>
            {purpose === "set"
              ? t("passwordLogin.setTitle")
              : t("passwordLogin.resetTitle")}
          </div>
          <p className={`text-ink-500 leading-[1.5] mt-1 mb-3 ${sz.small}`}>
            {purpose === "set"
              ? t("passwordLogin.setSub")
              : t("passwordLogin.resetSub")}
          </p>
          {/* Who the WhatsApp code comes from, said up front: the sender is Wcommerce, not the
              store, and a shopper who doesn't know that may ignore the message. */}
          <div
            className={`flex items-start gap-1.5 rounded-md bg-s-tint px-2.5 py-2 mb-2 text-ink-700 leading-[1.45] ${sz.small}`}
          >
            <IoLogoWhatsapp className="text-whatsapp flex-none mt-[1px]" size={15} />
            <span>
              <Trans
                i18nKey="passwordLogin.codeSentTo"
                values={{ number: maskMobile(country.dial, mobile) }}
                components={[
                  <b className="text-ink-900 font-[500]" />,
                  <b className="text-ink-900 font-[500]" />,
                ]}
              />{" "}
              <button
                type="button"
                onClick={() => goTo("login")}
                className="text-s-primary font-[500]"
              >
                {t("passwordLogin.change")}
              </button>
            </span>
          </div>
          <div
            className={`flex justify-center my-1.5 ${compact ? "gap-2" : "gap-2.5"}`}
            onPaste={onCodePaste}
          >
            {[0, 1, 2, 3].map((i) => {
              const d = code[i];
              return (
                <input
                  key={i}
                  ref={(el) => (boxRefs.current[i] = el)}
                  data-testid={`pw-code-${i}`}
                  aria-label={`${t("passwordLogin.code")} ${i + 1}`}
                  value={d}
                  maxLength={1}
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  disabled={busy}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onCodeKeyDown(i, e)}
                  className={`text-center font-[500] text-ink-900 outline-none border-[1.5px] ${
                    compact
                      ? `w-[42px] h-[48px] text-[19px] rounded-[10px] ${
                          d ? "border-ink-400 bg-white" : "border-wc-border bg-gray-100"
                        }`
                      : `w-12 h-14 text-[22px] rounded-md bg-s-tint ${
                          d ? "border-s-primary" : "border-wc-border"
                        }`
                  } focus:border-s-primary focus:bg-white focus:shadow-[0_0_0_3px_rgba(72,98,238,.12)]`}
                />
              );
            })}
          </div>
          <div className="mt-3">
            <Label>{t("passwordLogin.newPassword")}</Label>
            {PasswordInput({
              value: newPassword,
              onChange: (v) => {
                setNewPassword(v);
                setError("");
              },
              onEnter: submitCode,
              testId: "pw-new-password",
              autoComplete: "new-password",
              placeholder: t("passwordLogin.newPasswordPlaceholder"),
            })}
            <p className={`text-ink-400 mt-1 ${sz.small}`}>
              {t("passwordLogin.passwordHint")}
            </p>
          </div>
          <Message />
          <PrimaryBtn onClick={submitCode} disabled={codeValue.length !== 4 || busy}>
            {busy ? t("passwordLogin.saving") : t("passwordLogin.save")}
          </PrimaryBtn>
          <p className={`text-center text-ink-400 mt-3 ${sz.small}`}>
            {t("passwordLogin.didntGetIt")}{" "}
            {cooldown > 0 ? (
              <span className="font-[500]">
                {t("passwordLogin.resendIn", { seconds: cooldown })}
              </span>
            ) : (
              <button
                type="button"
                onClick={resend}
                className="text-s-primary font-[500]"
              >
                {t("passwordLogin.resend")}
              </button>
            )}
          </p>
        </div>
      )}

      <CountryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedCode={country.code}
        onSelect={(c) => {
          const next = findCountry(c);
          setCountry(next);
          setMobile((m) => m.slice(0, maxLen(next)));
        }}
      />
    </>
  );
};

export default PasswordLoginForm;
